import { MODULES, buildableCells, type StationState } from "@station-keeping/sim";
import type { ReactNode } from "react";
import { useGameUi, type Panel, type ToastTone } from "../game/store";
import { t } from "../i18n";
import type { UiIconName } from "../pixel/icons";
import { moduleName } from "./labels";
import { Icon, PixelText } from "./pixel";
import { Key, Led } from "./primitives";

interface DeckKey {
  panel: Panel;
  icon: UiIconName;
  label: "tab.build" | "tab.failures" | "tab.ships" | "tab.station";
  shortcut: string;
  /** Couleur du voyant de la touche. */
  led: string;
}

const KEYS: DeckKey[] = [
  { panel: "build", icon: "build", label: "tab.build", shortcut: "B", led: "var(--color-accent)" },
  { panel: "failures", icon: "failures", label: "tab.failures", shortcut: "P", led: "var(--color-warn)" },
  { panel: "ships", icon: "ships", label: "tab.ships", shortcut: "V", led: "var(--color-accent)" },
  { panel: "station", icon: "station", label: "tab.station", shortcut: "S", led: "var(--color-good)" },
];

interface KeySignal {
  count: number;
  /** Le voyant clignote : quelque chose presse derrière cette touche. */
  urgent: boolean;
  color?: string;
}

function signals(state: StationState): Partial<Record<Panel, KeySignal>> {
  const failed = state.modules.filter((module) => module.failure);
  const ships = state.ships.filter((ship) => ship.phase === "docked").length;
  const worst = failed.reduce((stage, module) => Math.max(stage, module.failure?.stage ?? 0), 0);
  return {
    ...(failed.length > 0
      ? { failures: { count: failed.length, urgent: true, color: worst >= 3 ? "var(--color-stop)" : worst === 2 ? "var(--color-alert)" : "var(--color-warn)" } }
      : {}),
    ...(ships > 0 ? { ships: { count: ships, urgent: false } } : {}),
  };
}

/**
 * Le pupitre : quatre touches qui ouvrent les panneaux. Une touche ouverte reste enfoncée, son voyant
 * allumé ; un voyant qui clignote signale ce qui presse (des pannes).
 */
export function Deck({ state, vertical }: { state: StationState; vertical: boolean }) {
  const panel = useGameUi((ui) => ui.panel);
  const sheetOpen = useGameUi((ui) => ui.selection !== null || ui.selectedShip !== null);
  const togglePanel = useGameUi((ui) => ui.togglePanel);
  const lights = signals(state);
  return (
    <nav
      className={`bezel pointer-events-auto ${vertical ? "flex flex-col gap-2" : "grid grid-cols-4 gap-1.5 pb-[max(calc(var(--px)*4),env(safe-area-inset-bottom))]"}`}
      aria-label={t("deck.label")}
    >
      {KEYS.map((key) => {
        const active = panel === key.panel && !sheetOpen;
        const signal = lights[key.panel];
        return (
          <button
            key={key.panel}
            type="button"
            className={`key flex-col gap-1 px-1 tracking-normal ${vertical ? "h-[5.5rem] w-[6.25rem]" : "h-[4.25rem]"}`}
            onClick={() => togglePanel(key.panel)}
            aria-pressed={active}
            aria-keyshortcuts={key.shortcut}
            title={`${t(key.label)} (${key.shortcut})`}
            data-tutorial={`deck-${key.panel}`}
          >
            <span className="flex h-[calc(var(--px)*7)] w-full items-center justify-between px-1">
              <Led on={active || Boolean(signal?.urgent)} blink={!active && Boolean(signal?.urgent)} color={signal?.color ?? key.led} />
              {signal ? (
                <span className="text-ink">
                  <PixelText text={String(signal.count)} label={t("deck.count", { count: signal.count })} />
                </span>
              ) : null}
            </span>
            <Icon name={key.icon} className={active ? "text-accent" : "text-ink"} />
            <span className="text-[0.875rem] leading-none">{t(key.label)}</span>
          </button>
        );
      })}
    </nav>
  );
}

/** Le panneau ouvert : collé au pupitre sur grand écran, tiroir au-dessus de lui sur téléphone. */
export function PanelFrame({ children, desktop }: { children: ReactNode; desktop: boolean }) {
  return (
    <section className={`wipe-in pointer-events-auto flex min-h-0 flex-col ${desktop ? "max-h-full w-[23rem]" : "max-h-[56vh]"}`}>
      <div className="bezel flex min-h-0 flex-col">
        <div className="min-h-0 overflow-y-auto overscroll-contain px-2 pb-3 pt-1">{children}</div>
      </div>
    </section>
  );
}

/** Bandeau du mode construction : une seule consigne, et la touche pour annuler. */
export function BuildBanner({ state }: { state: StationState }) {
  const buildType = useGameUi((ui) => ui.buildType);
  const cancelBuild = useGameUi((ui) => ui.cancelBuild);
  if (!buildType) return null;
  // Aucune case possible (zone pas encore atteinte) : on le dit, plutôt qu'un plan sans surbrillance.
  const blocked = buildableCells(state, buildType).length === 0;
  return (
    <div className="bezel pointer-events-auto mx-auto mt-2 w-fit max-w-[calc(100%-1rem)]" role="status">
      <div className="flex items-center gap-3 pl-1">
        <Led on blink color={blocked ? "var(--color-warn)" : "var(--color-accent)"} />
        <div className="min-w-0">
          <p className="text-accent">
            <PixelText text={t("build.placing", { module: moduleName(buildType) })} />
          </p>
          <p className={`mt-1 text-[0.9375rem] ${blocked ? "text-warn" : "text-muted"}`}>
            {blocked ? t("build.noCellHint", { zone: t(`zone.${MODULES[buildType].zone}`) }) : t("build.placingHint")}
          </p>
        </div>
        <Key size="sm" tone="quiet" onClick={cancelBuild}>
          {t("action.cancel")}
        </Key>
      </div>
    </div>
  );
}

const TOAST_LEDS: Record<ToastTone, string> = {
  info: "var(--color-accent)",
  good: "var(--color-good)",
  warn: "var(--color-warn)",
  alert: "var(--color-alert)",
};

/** Messages éphémères (pannes, chantiers finis, actions refusées), annoncés aux lecteurs d'écran. */
export function Toasts() {
  const toasts = useGameUi((ui) => ui.toasts);
  const dismiss = useGameUi((ui) => ui.dismissToast);
  return (
    <div className="pointer-events-none flex flex-col items-center gap-2" aria-live="polite" role="status">
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          onClick={() => dismiss(toast.id)}
          className="bezel wipe-in pointer-events-auto max-w-sm text-left"
        >
          <span className="flex items-center gap-3 px-1">
            <Led on color={TOAST_LEDS[toast.tone]} />
            <span className="text-[0.9375rem] text-ink">{toast.text}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
